import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Paper,
  Button,
  Avatar,
  Chip,
  Fade,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { 
  SmartToy, 
  ArrowForward, 
  Logout,
  Dashboard,
  CheckCircle,
  VpnKey,
  Delete,
  ContentCopy,
  Add,
  Edit
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { assistantService, authService, tokenService } from '../utils/api';
import { TTS_LANGUAGE_OPTIONS, TTS_MODEL_OPTIONS, TTS_PROVIDER_OPTIONS } from '../utils/ttsOptions';

function AssistantSelector() {
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssistantId, setSelectedAssistantId] = useState(null);
  const [userRole, setUserRole] = useState('');
  
  // Token management state
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  // Create assistant state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const initialCreateForm = {
    name: '',
    assistant_name: '',
    call_type: 'outbound',
    llm_provider: '',
    llm_model: '',
    llm_max_tokens: '',
    llm_temperature: '',
    tts_provider: 'cartesia',
    tts_model: '',
    tts_voice_id: '',
    tts_language: '',
    tts_speed: '',
    stt_provider: '',
    stt_model: '',
    stt_language: '',
    telephony_provider: '',
    user_speech_timeout: '',
    webhook_url: ''
  };
  const [createForm, setCreateForm] = useState(initialCreateForm);

  // Edit assistant state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAssistantId, setEditingAssistantId] = useState(null);
  const [editFormLoading, setEditFormLoading] = useState(false); // loading assistant data into form
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchAssistants();
    const role = authService.getUserRole();
    setUserRole(role || '');
  }, []);

  const fetchAssistants = async () => {
    try {
      const response = await assistantService.getMyAssistants();
      setAssistants(response.assistants || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch assistants');
    } finally {
      setLoading(false);
    }
  };

  const handleAssistantSelect = (assistant) => {
    setSelectedAssistantId(assistant.id);
    setTimeout(() => {
      assistantService.setSelectedAssistant(assistant);
      navigate(`/assistant/${assistant.id}/users`);
    }, 300);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const fetchTokens = async () => {
    setTokenLoading(true);
    try {
      const response = await tokenService.listTokens();
      setTokens(response.tokens || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch tokens');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleOpenTokenDialog = () => {
    setTokenDialogOpen(true);
    setCreatedToken(null);
    fetchTokens();
  };

  const handleCloseTokenDialog = () => {
    setTokenDialogOpen(false);
    setTokenName('');
    setCreatedToken(null);
  };

  const handleCreateToken = async () => {
    if (!tokenName.trim()) {
      toast.error('Please enter a token name');
      return;
    }

    setTokenLoading(true);
    try {
      const response = await tokenService.createToken(tokenName);
      setCreatedToken(response);
      setTokenName('');
      toast.success('Token created successfully!');
      fetchTokens();
    } catch (error) {
      toast.error(error.message || 'Failed to create token');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token? This action cannot be undone.')) {
      return;
    }

    try {
      await tokenService.deleteToken(tokenId);
      toast.success('Token deleted successfully');
      fetchTokens();
    } catch (error) {
      toast.error(error.message || 'Failed to delete token');
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success('Token copied to clipboard!');
  };

  const handleOpenCreateDialog = () => {
    setCreateForm(initialCreateForm);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildAssistantPayload = (form) => {
    const payload = {
      name: form.name?.trim(),
      assistant_name: form.assistant_name?.trim() || undefined,
      call_type: form.call_type || undefined,
      llm_provider: form.llm_provider?.trim() || undefined,
      llm_model: form.llm_model?.trim() || undefined,
      llm_max_tokens: form.llm_max_tokens === '' ? undefined : Number(form.llm_max_tokens),
      llm_temperature: form.llm_temperature === '' ? undefined : Number(form.llm_temperature),
      tts_provider: form.tts_provider?.trim() || undefined,
      tts_model: form.tts_model?.trim() || undefined,
      tts_voice_id: form.tts_voice_id?.trim() || undefined,
      tts_language: form.tts_language?.trim() || undefined,
      tts_speed: form.tts_speed === '' ? undefined : Number(form.tts_speed),
      stt_provider: form.stt_provider?.trim() || undefined,
      stt_model: form.stt_model?.trim() || undefined,
      stt_language: form.stt_language?.trim() || undefined,
      telephony_provider: form.telephony_provider?.trim() || undefined,
      user_speech_timeout: form.user_speech_timeout === '' ? undefined : Number(form.user_speech_timeout),
      webhook_url: form.webhook_url?.trim() || undefined
    };
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    return payload;
  };

  const handleCreateAssistant = async () => {
    if (!createForm.name?.trim()) {
      toast.error('Assistant name is required');
      return;
    }
    setCreateLoading(true);
    try {
      await assistantService.createAssistant(buildAssistantPayload(createForm));
      toast.success('Assistant created successfully');
      handleCloseCreateDialog();
      fetchAssistants();
    } catch (error) {
      toast.error(error.message || error.detail || 'Failed to create assistant');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenEditDialog = async (e, assistant) => {
    e.stopPropagation();
    setEditingAssistantId(assistant.id);
    setEditDialogOpen(true);
    setEditFormLoading(true);
    try {
      const data = await assistantService.getAssistant(assistant.id);
      setCreateForm({
        name: data.name || '',
        assistant_name: data.assistant_name || '',
        call_type: data.call_type || 'outbound',
        llm_provider: data.llm_provider || '',
        llm_model: data.llm_model || '',
        llm_max_tokens: data.llm_max_tokens ?? '',
        llm_temperature: data.llm_temperature ?? '',
        tts_provider: data.tts_provider || 'cartesia',
        tts_model: data.tts_model || '',
        tts_voice_id: data.tts_voice_id || '',
        tts_language: data.tts_language || '',
        tts_speed: data.tts_speed ?? '',
        stt_provider: data.stt_provider || '',
        stt_model: data.stt_model || '',
        stt_language: data.stt_language || '',
        telephony_provider: data.telephony_provider || '',
        user_speech_timeout: data.user_speech_timeout ?? '',
        webhook_url: data.webhook_url || ''
      });
    } catch (error) {
      toast.error(error.message || 'Failed to load assistant');
      setEditDialogOpen(false);
      setEditingAssistantId(null);
    } finally {
      setEditFormLoading(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingAssistantId(null);
  };

  const handleUpdateAssistant = async () => {
    if (!editingAssistantId || !createForm.name?.trim()) {
      toast.error('Assistant name is required');
      return;
    }
    setEditSaving(true);
    try {
      await assistantService.updateAssistant(editingAssistantId, buildAssistantPayload(createForm));
      toast.success('Assistant updated successfully');
      handleCloseEditDialog();
      fetchAssistants();
    } catch (error) {
      toast.error(error.message || error.detail || 'Failed to update assistant');
    } finally {
      setEditSaving(false);
    }
  };

  const isAssistantFormOpen = createDialogOpen || editDialogOpen;
  const isEditMode = !!editingAssistantId;

  const handleCloseAssistantDialog = () => {
    if (isEditMode) handleCloseEditDialog();
    else handleCloseCreateDialog();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated gradient background */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: `linear-gradient(
              135deg,
              rgba(67, 97, 238, 0.2) 0%,
              rgba(115, 139, 255, 0.3) 25%,
              rgba(255, 255, 255, 0.1) 50%,
              rgba(67, 97, 238, 0.4) 75%,
              rgba(37, 65, 178, 0.5) 100%
            )`,
            backgroundSize: '400% 400%',
            animation: 'wave 8s ease-in-out infinite',
            '@keyframes wave': {
              '0%, 100%': {
                backgroundPosition: '0% 50%'
              },
              '50%': {
                backgroundPosition: '100% 50%'
              }
            }
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: '#1a1a1a' }}>
            Loading assistants...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated gradient background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          background: `linear-gradient(
            135deg,
            rgba(67, 97, 238, 0.2) 0%,
            rgba(115, 139, 255, 0.3) 25%,
            rgba(255, 255, 255, 0.1) 50%,
            rgba(67, 97, 238, 0.4) 75%,
            rgba(37, 65, 178, 0.5) 100%
          )`,
          backgroundSize: '400% 400%',
          animation: 'wave 8s ease-in-out infinite',
          '@keyframes wave': {
            '0%, 100%': {
              backgroundPosition: '0% 50%'
            },
            '50%': {
              backgroundPosition: '100% 50%'
            }
          }
        }}
      />

      {/* Header with logout */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: 3,
          zIndex: 10
        }}
      >
        <Tooltip title="Logout">
          <IconButton
            onClick={handleLogout}
            sx={{
              backgroundColor: 'rgba(67, 97, 238, 0.1)',
              color: '#4361ee',
              border: '1px solid rgba(67, 97, 238, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(67, 97, 238, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(67, 97, 238, 0.25)'
              }
            }}
          >
            <Logout />
          </IconButton>
        </Tooltip>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 2 }}>
        {/* Header Section */}
        <Fade in timeout={600}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto',
                mb: 3,
                backgroundColor: 'rgba(67, 97, 238, 0.1)',
                border: '2px solid rgba(67, 97, 238, 0.2)'
              }}
            >
              <Dashboard sx={{ fontSize: 40, color: '#4361ee' }} />
            </Avatar>
            
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Choose Your Assistant
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#6b7280',
                fontWeight: 400,
                lineHeight: 1.6,
                maxWidth: 600,
                margin: '0 auto'
              }}
            >
              Select an assistant to manage users and settings
            </Typography>
          </Box>
        </Fade>

        {/* Assistants Grid */}
        <Grid container spacing={4} justifyContent="center">
          {assistants.map((assistant, index) => (
            <Grid item xs={12} sm={6} md={4} key={assistant.id}>
              <Fade in timeout={800 + index * 200}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: selectedAssistantId === assistant.id 
                      ? '#4361ee'
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: selectedAssistantId === assistant.id 
                      ? '2px solid #4361ee'
                      : '1px solid rgba(229, 231, 235, 0.5)',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: selectedAssistantId === assistant.id 
                      ? '0 8px 25px rgba(67, 97, 238, 0.25)'
                      : '0 4px 15px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: selectedAssistantId === assistant.id 
                        ? '0 12px 35px rgba(67, 97, 238, 0.3)'
                        : '0 8px 25px rgba(0, 0, 0, 0.15)',
                      '& .assistant-arrow': {
                        transform: 'translateX(8px)',
                        opacity: 1
                      }
                    }
                  }}
                  onClick={() => handleAssistantSelect(assistant)}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
                    {/* Selection indicator */}
                    {selectedAssistantId === assistant.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16
                        }}
                      >
                        <CheckCircle sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                    )}

                    {/* Assistant Icon */}
                    <Avatar
                      sx={{
                        width: 70,
                        height: 70,
                        margin: '0 auto',
                        mb: 2,
                        backgroundColor: selectedAssistantId === assistant.id 
                          ? 'rgba(255, 255, 255, 0.2)' 
                          : '#4361ee',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <SmartToy sx={{ 
                        fontSize: 35, 
                        color: 'white'
                      }} />
                    </Avatar>

                    {/* Assistant Name */}
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: selectedAssistantId === assistant.id ? 'white' : '#1a1a1a',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {assistant.name}
                    </Typography>

                    {/* Assistant Display Name */}
                    {assistant.assistant_name && (
                      <Typography
                        variant="body2"
                        sx={{ 
                          mb: 3,
                          fontWeight: 400,
                          color: selectedAssistantId === assistant.id 
                            ? 'rgba(255, 255, 255, 0.8)' 
                            : '#6b7280'
                        }}
                      >
                        {assistant.assistant_name}
                      </Typography>
                    )}

                    {/* Status Chip */}
                    <Chip
                      label={assistant.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        mb: 2,
                        backgroundColor: selectedAssistantId === assistant.id 
                          ? 'rgba(255, 255, 255, 0.2)'
                          : assistant.is_active ? '#10b981' : '#ef4444',
                        color: 'white',
                        fontWeight: 500,
                        border: selectedAssistantId === assistant.id 
                          ? '1px solid rgba(255, 255, 255, 0.3)'
                          : 'none'
                      }}
                    />

                    {/* Call Type */}
                    {assistant.call_type && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          color: selectedAssistantId === assistant.id 
                            ? 'rgba(255, 255, 255, 0.7)' 
                            : '#9ca3af',
                          textTransform: 'capitalize'
                        }}
                      >
                        {assistant.call_type}
                      </Typography>
                    )}

                    {/* Assistant ID (below call type) */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: assistant.call_type ? 2 : 3,
                        fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace',
                        fontSize: '0.75rem',
                        lineHeight: 1.25,
                        wordBreak: 'break-all',
                        color: selectedAssistantId === assistant.id
                          ? 'rgba(255, 255, 255, 0.75)'
                          : '#94a3b8'
                      }}
                    >
                      {assistant.id}
                    </Typography>

                    {/* Edit button - super_admin only, styled like Users/Prompts */}
                    {userRole === 'super_admin' && (
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<Edit sx={{ fontSize: 18 }} />}
                        onClick={(e) => handleOpenEditDialog(e, assistant)}
                        sx={{
                          mb: 1.5,
                          borderRadius: '6px',
                          py: 1,
                          px: 1.5,
                          minHeight: 40,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          color: selectedAssistantId === assistant.id ? 'rgba(255,255,255,0.9)' : '#64748b',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            bgcolor: selectedAssistantId === assistant.id ? 'rgba(255,255,255,0.1)' : '#f1f5f9'
                          }
                        }}
                      >
                        Edit
                      </Button>
                    )}

                    {/* Action Button */}
                    <Button
                      variant={selectedAssistantId === assistant.id ? "outlined" : "contained"}
                      fullWidth
                      endIcon={
                        <ArrowForward 
                          className="assistant-arrow"
                          sx={{ 
                            transition: 'all 0.3s ease',
                            opacity: 0.7
                          }} 
                        />
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'none',
                        ...(selectedAssistantId === assistant.id ? {
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)'
                          }
                        } : {
                          backgroundColor: '#4361ee',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#2541b2',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(67, 97, 238, 0.25)'
                          }
                        })
                      }}
                    >
                      {selectedAssistantId === assistant.id ? 'Selected' : 'Select Assistant'}
                    </Button>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {assistants.length === 0 && (
          <Fade in timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                mt: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 2,
                border: '1px solid rgba(229, 231, 235, 0.5)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
              }}
            >
              <SmartToy sx={{ fontSize: 64, color: '#6b7280', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                No Assistants Available
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
                You don't have access to any assistants yet.
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Please contact your administrator to get access to an assistant.
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* Create Assistant & API Tokens - Only for Super Admins */}
        {userRole === 'super_admin' && (
          <Fade in timeout={1200}>
            <Box sx={{ mt: 6, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreateDialog}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  backgroundColor: '#4361ee',
                  '&:hover': { backgroundColor: '#2541b2' }
                }}
              >
                Create Assistant
              </Button>
              <Button
                variant="outlined"
                startIcon={<VpnKey />}
                onClick={handleOpenTokenDialog}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#eff6ff',
                    color: '#3b82f6'
                  }
                }}
              >
                Manage API Tokens
              </Button>
            </Box>
          </Fade>
        )}
      </Container>

      {/* Create / Edit Assistant Dialog */}
      <Dialog
        open={isAssistantFormOpen}
        onClose={handleCloseAssistantDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SmartToy sx={{ color: '#4361ee' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#0f172a' }}>
              {isEditMode ? 'Edit Assistant' : 'Create Assistant'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isEditMode && editFormLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
          <Grid container spacing={2}>
            {/* Basic */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
                Basic
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label="Name"
                value={createForm.name}
                onChange={(e) => handleCreateFormChange('name', e.target.value)}
                placeholder="Assistant name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Display name"
                value={createForm.assistant_name}
                onChange={(e) => handleCreateFormChange('assistant_name', e.target.value)}
                placeholder="Optional display name"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Call type</InputLabel>
                <Select
                  value={createForm.call_type}
                  label="Call type"
                  onChange={(e) => handleCreateFormChange('call_type', e.target.value)}
                >
                  <MenuItem value="outbound">Outbound</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* LLM */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                LLM
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                disabled
                label="LLM Provider"
                value={createForm.llm_provider}
                onChange={(e) => handleCreateFormChange('llm_provider', e.target.value)}
                placeholder="e.g. openai"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="LLM Model"
                value={createForm.llm_model}
                onChange={(e) => handleCreateFormChange('llm_model', e.target.value)}
                placeholder="e.g. gpt-4"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                disabled
                label="Max tokens"
                value={createForm.llm_max_tokens}
                onChange={(e) => handleCreateFormChange('llm_max_tokens', e.target.value)}
                placeholder="e.g. 1024"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Temperature"
                value={createForm.llm_temperature}
                onChange={(e) => handleCreateFormChange('llm_temperature', e.target.value)}
                placeholder="e.g. 0.7"
                inputProps={{ min: 0, max: 2, step: 0.1 }}
              />
            </Grid>

            {/* TTS */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                TTS
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled>
                <InputLabel>TTS Provider</InputLabel>
                <Select
                  value={createForm.tts_provider}
                  label="TTS Provider"
                  onChange={(e) => handleCreateFormChange('tts_provider', e.target.value)}
                >
                  {TTS_PROVIDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled>
                <InputLabel>TTS Model</InputLabel>
                <Select
                  value={createForm.tts_model}
                  label="TTS Model"
                  onChange={(e) => handleCreateFormChange('tts_model', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {TTS_MODEL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="TTS Voice ID"
                value={createForm.tts_voice_id}
                onChange={(e) => handleCreateFormChange('tts_voice_id', e.target.value)}
                placeholder="e.g. voice_abc123"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled>
                <InputLabel>TTS Language</InputLabel>
                <Select
                  value={createForm.tts_language}
                  label="TTS Language"
                  onChange={(e) => handleCreateFormChange('tts_language', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {TTS_LANGUAGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="TTS Speed"
                value={createForm.tts_speed}
                onChange={(e) => handleCreateFormChange('tts_speed', e.target.value)}
                placeholder="e.g. 1.0"
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            {/* STT */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                STT
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                disabled
                label="STT Provider"
                value={createForm.stt_provider}
                onChange={(e) => handleCreateFormChange('stt_provider', e.target.value)}
                placeholder="e.g. deepgram"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                disabled
                label="STT Model"
                value={createForm.stt_model}
                onChange={(e) => handleCreateFormChange('stt_model', e.target.value)}
                placeholder="e.g. nova-2"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                disabled
                label="STT Language"
                value={createForm.stt_language}
                onChange={(e) => handleCreateFormChange('stt_language', e.target.value)}
                placeholder="e.g. en"
              />
            </Grid>

            {/* Telephony */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                Telephony
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                disabled
                label="Telephony Provider"
                value={createForm.telephony_provider}
                onChange={(e) => handleCreateFormChange('telephony_provider', e.target.value)}
                placeholder="e.g. twilio"
              />
            </Grid>

            {/* General */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                General
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="User speech timeout (s)"
                value={createForm.user_speech_timeout}
                onChange={(e) => handleCreateFormChange('user_speech_timeout', e.target.value)}
                placeholder="e.g. 0.5"
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            {/* Webhook */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mt: 1, mb: 1 }}>
                Webhook
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Webhook URL"
                value={createForm.webhook_url}
                onChange={(e) => handleCreateFormChange('webhook_url', e.target.value)}
                placeholder="https://example.com/webhook"
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={handleCloseAssistantDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={isEditMode ? handleUpdateAssistant : handleCreateAssistant}
            disabled={(isEditMode ? editSaving : createLoading) || !createForm.name?.trim()}
            startIcon={(isEditMode ? editSaving : createLoading) ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ textTransform: 'none', backgroundColor: '#4361ee', '&:hover': { backgroundColor: '#2541b2' } }}
          >
            {isEditMode ? (editSaving ? 'Saving…' : 'Save') : (createLoading ? 'Creating…' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Management Dialog */}
      <Dialog
        open={tokenDialogOpen}
        onClose={handleCloseTokenDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VpnKey sx={{ color: '#3b82f6' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#0f172a' }}>
              API Tokens
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Create Token Section */}
          {createdToken && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                bgcolor: '#dcfce7',
                border: '1px solid #86efac',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534', mb: 1, fontSize: '0.875rem' }}>
                Token created successfully!
              </Typography>
              <Typography variant="caption" sx={{ color: '#15803d', mb: 1.5, display: 'block', fontSize: '0.75rem' }}>
                Save this token securely. It won't be shown again.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  value={createdToken.token}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontFamily: 'monospace',
                      fontSize: '0.8125rem',
                      bgcolor: '#f0fdf4'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#86efac'
                      }
                    }
                  }}
                />
                <IconButton
                  onClick={() => handleCopyToken(createdToken.token)}
                  sx={{
                    bgcolor: '#16a34a',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#15803d'
                    }
                  }}
                >
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Paper>
          )}

          {/* Create New Token Form */}
          <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', mb: 2, fontSize: '0.875rem' }}>
              Create New Token
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Token name (e.g., Production API)"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                disabled={tokenLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    '& fieldset': {
                      borderColor: '#e2e8f0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: 1
                    }
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={tokenLoading ? <CircularProgress size={16} color="inherit" /> : <Add />}
                onClick={handleCreateToken}
                disabled={tokenLoading || !tokenName.trim()}
                sx={{
                  bgcolor: '#3b82f6',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  height: 40,
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#2563eb',
                    boxShadow: 'none'
                  }
                }}
              >
                Create
              </Button>
            </Box>
          </Box>

          {/* Tokens List */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', mb: 2, fontSize: '0.875rem' }}>
            Your Tokens
          </Typography>

          {tokenLoading && tokens.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : tokens.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 4, fontSize: '0.875rem' }}>
              No tokens yet. Create one to get started.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                      Token
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                      Created
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                      Last Used
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map((token) => (
                    <TableRow
                      key={token.id}
                      sx={{
                        '&:hover': { bgcolor: '#f8fafc' },
                        '& td': { borderBottom: '1px solid #f1f5f9' }
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a', fontSize: '0.8125rem' }}>
                          {token.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: '#475569',
                            fontSize: '0.8125rem'
                          }}
                        >
                          {token.token_preview}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                          {new Date(token.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                          {token.last_used_at
                            ? new Date(token.last_used_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteToken(token.id)}
                            sx={{
                              color: '#64748b',
                              '&:hover': {
                                bgcolor: '#fee2e2',
                                color: '#dc2626'
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleCloseTokenDialog}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssistantSelector;
